<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";
import { useProjects, useProjectWorkflows } from "@/queries/projects";
import {
  useSchedule,
  useScheduleRuns,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
  usePauseSchedule,
  useResumeSchedule,
  useRunScheduleNow,
  usePreviewNextRuns,
  RUN_LOG_CAP,
} from "@/queries/schedules";
import type { ScheduleKind, CreateScheduleRequest, UpdateScheduleRequest } from "@/types/schedule-types";
import { useAlertStore } from "@/stores/alerts-store";
import FormAlert from "@/components/FormAlert.vue";
import {
  toCron,
  fromCron,
  WEEKDAYS,
  DEFAULT_SIMPLE,
  type SimpleSchedule,
} from "@/views/scheduler/cron-builder";

const props = withDefaults(
  defineProps<{
    /** Null/"new" = create mode; otherwise the schedule being edited. */
    scheduleId?: string | null;
    /** When set, the schedule is created for this project — the project picker is hidden/locked. */
    lockedProjectId?: string | null;
  }>(),
  { scheduleId: null, lockedProjectId: null }
);

const emit = defineEmits<{
  saved: [scheduleId: string | null];
  cancelled: [];
  deleted: [];
}>();

const { trackView } = useRecentlyViewed();

const scheduleIdRef = computed(() => props.scheduleId ?? "");
const isEditMode = computed(() => !!props.scheduleId && props.scheduleId !== "new");

const frequencyLabels: { value: SimpleSchedule["frequency"]; label: string; icon: string }[] = [
  { value: "hourly", label: "Hourly", icon: "bi-arrow-clockwise" },
  { value: "daily", label: "Daily", icon: "bi-brightness-high" },
  { value: "weekly", label: "Weekly", icon: "bi-calendar-week" },
  { value: "monthly", label: "Monthly", icon: "bi-calendar-month" },
];

// Curated IANA zones grouped by region. The backend accepts any valid IANA id
// (TimeZoneInfo.FindSystemTimeZoneById), so this is a UX subset, not a hard limit.
const timezoneGroups: { label: string | null; zones: [string, string][] }[] = [
  { label: null, zones: [["UTC", "UTC"]] },
  { label: "Americas", zones: [
    ["America/Los_Angeles", "Los Angeles (Pacific)"],
    ["America/Denver", "Denver (Mountain)"],
    ["America/Chicago", "Chicago (Central)"],
    ["America/New_York", "New York (Eastern)"],
    ["America/Toronto", "Toronto"],
    ["America/Sao_Paulo", "São Paulo"],
  ] },
  { label: "Europe & Africa", zones: [
    ["Europe/London", "London"],
    ["Europe/Berlin", "Berlin (Central European)"],
    ["Europe/Athens", "Athens (Eastern European)"],
    ["Europe/Moscow", "Moscow"],
    ["Africa/Johannesburg", "Johannesburg"],
  ] },
  { label: "Asia & Pacific", zones: [
    ["Asia/Dubai", "Dubai"],
    ["Asia/Kolkata", "Kolkata (India)"],
    ["Asia/Singapore", "Singapore"],
    ["Asia/Shanghai", "Shanghai"],
    ["Asia/Tokyo", "Tokyo"],
    ["Australia/Sydney", "Sydney"],
    ["Pacific/Auckland", "Auckland"],
  ] },
];

/** Current UTC offset for an IANA zone, e.g. "GMT−4" / "GMT+5:30". Reflects the
 *  user's current date, so it tracks DST automatically. */
function zoneOffsetLabel(zoneId: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zoneId,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const tzName = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    // Some runtimes emit a plain hyphen for negative offsets; use a real minus sign.
    return tzName.replace("-", "−") || "GMT+0";
  } catch {
    return "";
  }
}

/** The browser's IANA zone if it's one of our curated options, else "UTC".
 *  Used as the default for new schedules only; edit mode loads the saved zone. */
function defaultTimeZone(): string {
  const all = timezoneGroups.flatMap((g) => g.zones.map(([id]) => id));
  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return all.includes(browser) ? browser : "UTC";
  } catch {
    return "UTC";
  }
}

/** timezoneGroups with the live offset appended to each option's display label. */
const timezoneGroupsWithOffsets = computed(() =>
  timezoneGroups.map((g) => ({
    label: g.label,
    zones: g.zones.map(([id, name]) => {
      const offset = zoneOffsetLabel(id);
      return [id, offset ? `${name} (${offset})` : name] as [string, string];
    }),
  }))
);

interface ScheduleFormData {
  projectId: string;
  workflowId: string;
  name: string;
  description: string;
  kind: ScheduleKind;
  cronMode: "simple" | "cron";
  /** Structured fields driving Simple mode; composed into a cron via toCron(). */
  simple: SimpleSchedule;
  cronExpression: string;
  timeZoneId: string;
  oneOffRunAtLocal: string;
  startDateLocal: string;
  endDateLocal: string;
  allowConcurrentRuns: boolean;
}

const form = ref<ScheduleFormData>({
  projectId: props.lockedProjectId ?? "",
  workflowId: "",
  name: "",
  description: "",
  kind: "Recurring",
  cronMode: "simple",
  simple: { ...DEFAULT_SIMPLE },
  cronExpression: "0 9 * * *",
  timeZoneId: defaultTimeZone(),
  oneOffRunAtLocal: "",
  startDateLocal: "",
  endDateLocal: "",
  allowConcurrentRuns: false,
});

const errors = ref<Record<string, string>>({});
const showDeleteModal = ref(false);
const nextRuns = ref<string[]>([]);
const previewError = ref<string | null>(null);
// Monotonic token to discard out-of-order preview responses: editing the cron fires a request per
// keystroke, and a slower earlier response must not clobber a newer one.
let previewRequestId = 0;

const { projects } = useProjects();
const projectIdRef = computed(() => form.value.projectId);
const workflowsEnabled = computed(() => !!form.value.projectId);
const { projectWorkflows } = useProjectWorkflows(projectIdRef, workflowsEnabled);

const { schedule, scheduleLoading } = useSchedule(scheduleIdRef);
const { scheduleRuns } = useScheduleRuns(scheduleIdRef);

const alertStore = useAlertStore();

const { createSchedule, createSchedulePending, createScheduleError, createScheduleReset } = useCreateSchedule();
const { updateSchedule, updateSchedulePending, updateScheduleError, updateScheduleReset } = useUpdateSchedule();
const { deleteScheduleAsync } = useDeleteSchedule();
const { pauseScheduleAsync } = usePauseSchedule();
const { resumeScheduleAsync } = useResumeSchedule();
const { runScheduleNowAsync } = useRunScheduleNow();
const previewNextRuns = usePreviewNextRuns();

// Save uses the non-throwing `mutate` + reactive error bound to <FormAlert> (the form convention);
// the spinner and error reflect whichever of create/update is active.
const saving = computed(() => createSchedulePending.value || updateSchedulePending.value);
const saveError = computed(() => createScheduleError.value || updateScheduleError.value);

/** Run a one-shot schedule action, surfacing any failure as an error alert (per queries/CONVENTIONS.md). */
async function withErrorAlert(title: string, fallback: string, action: () => Promise<unknown>) {
  try {
    await action();
  } catch (e) {
    alertStore.addError(title, e instanceof Error ? e.message : fallback);
  }
}

const selectedProjectName = computed(
  () => projects.value.find((p) => p.id === form.value.projectId)?.name ?? "the selected project"
);

const effectiveCron = computed(() => {
  if (form.value.kind !== "Recurring") return "";
  return form.value.cronMode === "simple" ? toCron(form.value.simple) : form.value.cronExpression;
});

/** Human summary of the Simple selection, shown beneath the builder. */
const simpleSummary = computed(() => {
  const s = form.value.simple;
  switch (s.frequency) {
    case "hourly":
      return `Every hour at ${s.minute} minute${s.minute === 1 ? "" : "s"} past the hour.`;
    case "daily":
      return `Every day at ${formatTime(s.time)}.`;
    case "weekly": {
      const names = WEEKDAYS.filter((d) => s.weekdays.includes(d.value)).map((d) => d.short);
      return names.length ? `Every week on ${names.join(", ")} at ${formatTime(s.time)}.` : "Pick at least one day.";
    }
    case "monthly":
      return `Every month on day ${s.dayOfMonth} at ${formatTime(s.time)}.`;
  }
});

function toggleWeekday(value: number) {
  const days = form.value.simple.weekdays;
  form.value.simple.weekdays = days.includes(value) ? days.filter((d) => d !== value) : [...days, value];
}

watch(
  () => [effectiveCron.value, form.value.timeZoneId, form.value.kind, form.value.endDateLocal],
  () => void refreshPreview()
);

// Switching Simple → Advanced carries the composed cron into the editable field so the user picks up
// where Simple left off rather than seeing a stale value.
watch(
  () => form.value.cronMode,
  (mode) => {
    if (mode === "cron" && form.value.kind === "Recurring") form.value.cronExpression = toCron(form.value.simple);
  }
);

async function refreshPreview() {
  previewError.value = null;
  nextRuns.value = [];
  if (form.value.kind !== "Recurring" || !effectiveCron.value) return;
  const requestId = ++previewRequestId;
  try {
    const result = await previewNextRuns({
      cronExpression: effectiveCron.value,
      timeZoneId: form.value.timeZoneId,
      count: 5,
      endDateUtc: form.value.endDateLocal ? new Date(form.value.endDateLocal).toISOString() : null,
    });
    if (requestId !== previewRequestId) return; // a newer request superseded this one
    nextRuns.value = result;
  } catch (e: any) {
    if (requestId !== previewRequestId) return; // stale error from a superseded request
    previewError.value = e?.message ?? "Could not preview this schedule.";
  }
}

onMounted(() => {
  if (isEditMode.value) return;
  void refreshPreview();
});

watch(
  schedule,
  (s) => {
    if (!s) return;
    const decomposed = s.cronExpression ? fromCron(s.cronExpression) : null;
    form.value = {
      projectId: s.projectId,
      workflowId: s.workflowId,
      name: s.name,
      description: s.description,
      kind: s.kind,
      cronMode: decomposed ? "simple" : "cron",
      simple: decomposed ?? { ...DEFAULT_SIMPLE },
      cronExpression: s.cronExpression || "0 9 * * *",
      timeZoneId: s.timeZoneId,
      oneOffRunAtLocal: s.oneOffRunAtUtc ? toLocalInput(s.oneOffRunAtUtc) : "",
      startDateLocal: s.startDateUtc ? toLocalInput(s.startDateUtc) : "",
      endDateLocal: s.endDateUtc ? toLocalInput(s.endDateUtc) : "",
      allowConcurrentRuns: s.allowConcurrentRuns,
    };
    trackView({
      id: s.id, name: s.name, type: "schedule",
      route: `/scheduler/edit/${s.id}`,
      description: s.description || "Scheduled workflow", icon: "bi-calendar",
    });
    void refreshPreview();
  },
  { immediate: true }
);

/** "18:00" → a locale-friendly "6:00 PM"; falls back to the raw value if unparseable. */
function formatTime(time: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return time;
  const d = new Date();
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}
function localToIso(local: string): string | null {
  return local ? new Date(local).toISOString() : null;
}

function validateForm(): boolean {
  errors.value = {};
  if (!form.value.projectId) errors.value.projectId = "Project is required";
  if (!form.value.workflowId) errors.value.workflowId = "Workflow is required";
  if (!form.value.name) errors.value.name = "Name is required";

  if (form.value.kind === "Recurring") {
    if (form.value.cronMode === "simple" && form.value.simple.frequency === "weekly" && form.value.simple.weekdays.length === 0)
      errors.value.weekdays = "Pick at least one day of the week";
    else if (!effectiveCron.value) errors.value.cronExpression = "A cron expression is required";
    else if (effectiveCron.value.trim().split(/\s+/).length !== 5)
      errors.value.cronExpression = "Cron expression must have 5 fields";
  } else {
    if (!form.value.oneOffRunAtLocal) errors.value.oneOffRunAtLocal = "A run time is required";
    else if (new Date(form.value.oneOffRunAtLocal) <= new Date())
      errors.value.oneOffRunAtLocal = "The run time must be in the future";
  }
  return Object.keys(errors.value).length === 0;
}

function saveSchedule() {
  if (!validateForm()) return;
  if (isEditMode.value) {
    const payload: UpdateScheduleRequest = {
      name: form.value.name,
      kind: form.value.kind,
      timeZoneId: form.value.timeZoneId,
      description: form.value.description,
      cronExpression: form.value.kind === "Recurring" ? effectiveCron.value : null,
      oneOffRunAtUtc: form.value.kind === "OneOff" ? localToIso(form.value.oneOffRunAtLocal) : null,
      startDateUtc: form.value.kind === "Recurring" ? localToIso(form.value.startDateLocal) : null,
      endDateUtc: form.value.kind === "Recurring" ? localToIso(form.value.endDateLocal) : null,
      allowConcurrentRuns: form.value.allowConcurrentRuns,
    };
    updateSchedule({ id: props.scheduleId!, payload }, { onSuccess: () => emit("saved", props.scheduleId!) });
  } else {
    const payload: CreateScheduleRequest = {
      projectId: form.value.projectId,
      workflowId: form.value.workflowId,
      name: form.value.name,
      kind: form.value.kind,
      timeZoneId: form.value.timeZoneId,
      description: form.value.description,
      cronExpression: form.value.kind === "Recurring" ? effectiveCron.value : null,
      oneOffRunAtUtc: form.value.kind === "OneOff" ? localToIso(form.value.oneOffRunAtLocal) : null,
      startDateUtc: form.value.kind === "Recurring" ? localToIso(form.value.startDateLocal) : null,
      endDateUtc: form.value.kind === "Recurring" ? localToIso(form.value.endDateLocal) : null,
      allowConcurrentRuns: form.value.allowConcurrentRuns,
    };
    createSchedule(payload, {
      onSuccess: (newId) => emit("saved", typeof newId === "string" ? newId : null),
    });
  }
}

function dismissSaveError() {
  createScheduleReset();
  updateScheduleReset();
}

async function confirmDelete() {
  showDeleteModal.value = false;
  await withErrorAlert("Delete failed", "Unable to delete the schedule.", async () => {
    await deleteScheduleAsync(props.scheduleId!);
    emit("deleted");
  });
}

async function toggleScheduleStatus() {
  if (!schedule.value) return;
  await withErrorAlert("Action failed", "Unable to update the schedule.", () =>
    schedule.value!.status === "Paused" ? resumeScheduleAsync(props.scheduleId!) : pauseScheduleAsync(props.scheduleId!)
  );
}

async function runNow() {
  await withErrorAlert("Run failed", "Unable to run the schedule now.", () => runScheduleNowAsync(props.scheduleId!));
}
</script>

<template>
  <div>
    <!-- Loading State -->
    <div v-if="isEditMode && scheduleLoading" class="text-center py-5">
      <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
      <p class="mt-3 text-muted">Loading schedule...</p>
    </div>

    <div v-else class="row">
      <!-- Main Form -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title">Schedule Details</h2>
            <form id="schedule-form" @submit.prevent="saveSchedule">
              <!-- Project (hidden when locked to a project context) -->
              <div v-if="!lockedProjectId" class="row mb-3">
                <label for="projectId" class="col-sm-3 col-form-label">Project <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                  <select
                    id="projectId"
                    class="form-select"
                    v-model="form.projectId"
                    :class="{ 'is-invalid': errors.projectId }"
                    :disabled="isEditMode"
                    @change="form.workflowId = ''"
                  >
                    <option value="">Select a project</option>
                    <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                  <div v-if="errors.projectId" class="invalid-feedback">{{ errors.projectId }}</div>
                </div>
              </div>

              <!-- Workflow (scoped to the chosen/locked project) -->
              <div class="row mb-3">
                <label for="workflowId" class="col-sm-3 col-form-label">Workflow <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                  <select
                    id="workflowId"
                    class="form-select"
                    v-model="form.workflowId"
                    :class="{ 'is-invalid': errors.workflowId }"
                    :disabled="isEditMode || !form.projectId"
                  >
                    <option value="">Select a workflow</option>
                    <option v-for="w in projectWorkflows" :key="w.id" :value="w.id">{{ w.name }}</option>
                  </select>
                  <div v-if="isEditMode" class="form-text">Project and workflow cannot be changed after creation.</div>
                  <div v-else-if="!form.projectId" class="form-text">Choose a project first.</div>
                  <div v-if="errors.workflowId" class="invalid-feedback">{{ errors.workflowId }}</div>
                </div>
              </div>

              <!-- Config note -->
              <div class="row mb-3">
                <div class="col-sm-9 offset-sm-3">
                  <p class="text-muted small mb-0">
                    <i class="bi bi-info-circle me-1"></i>
                    This schedule runs the workflow using <strong>{{ selectedProjectName }}</strong>'s
                    configuration (wizard answers, connections, and datastores).
                  </p>
                </div>
              </div>

              <!-- Name -->
              <div class="row mb-3">
                <label for="name" class="col-sm-3 col-form-label">Name <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                  <input id="name" type="text" class="form-control" v-model="form.name"
                    :class="{ 'is-invalid': errors.name }" placeholder="e.g. Nightly data sync" />
                  <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                </div>
              </div>

              <!-- Description -->
              <div class="row mb-3">
                <label for="description" class="col-sm-3 col-form-label">Description</label>
                <div class="col-sm-9">
                  <textarea id="description" class="form-control" rows="2" v-model="form.description"
                    placeholder="Describe what this schedule does"></textarea>
                </div>
              </div>

              <!-- Kind -->
              <div class="row mb-3">
                <label class="col-sm-3 col-form-label">Type</label>
                <div class="col-sm-9">
                  <div class="btn-group" role="group">
                    <input type="radio" class="btn-check" id="kind-recurring" value="Recurring" v-model="form.kind" />
                    <label class="btn btn-outline-primary" for="kind-recurring"><i class="bi bi-arrow-repeat me-1"></i>Recurring</label>
                    <input type="radio" class="btn-check" id="kind-oneoff" value="OneOff" v-model="form.kind" />
                    <label class="btn btn-outline-primary" for="kind-oneoff"><i class="bi bi-1-circle me-1"></i>One-off</label>
                  </div>
                </div>
              </div>

              <!-- One-off run time -->
              <div v-if="form.kind === 'OneOff'" class="row mb-3">
                <label for="oneOffRunAt" class="col-sm-3 col-form-label">Run at <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                  <input id="oneOffRunAt" type="datetime-local" class="form-control" v-model="form.oneOffRunAtLocal"
                    :class="{ 'is-invalid': errors.oneOffRunAtLocal }" />
                  <div v-if="errors.oneOffRunAtLocal" class="invalid-feedback">{{ errors.oneOffRunAtLocal }}</div>
                </div>
              </div>

              <!-- Recurring config -->
              <template v-if="form.kind === 'Recurring'">
                <div class="row mb-3">
                  <label class="col-sm-3 col-form-label">Schedule</label>
                  <div class="col-sm-9">
                    <div class="btn-group" role="group">
                      <input type="radio" class="btn-check" id="cron-simple" value="simple" v-model="form.cronMode" />
                      <label class="btn btn-outline-primary" for="cron-simple"><i class="bi bi-clock me-1"></i>Simple</label>
                      <input type="radio" class="btn-check" id="cron-advanced" value="cron" v-model="form.cronMode" />
                      <label class="btn btn-outline-primary" for="cron-advanced"><i class="bi bi-gear me-1"></i>Advanced (Cron)</label>
                    </div>
                  </div>
                </div>

                <template v-if="form.cronMode === 'simple'">
                  <!-- Frequency -->
                  <div class="row mb-3">
                    <label class="col-sm-3 col-form-label">Frequency</label>
                    <div class="col-sm-9">
                      <div class="btn-group flex-wrap" role="group">
                        <template v-for="f in frequencyLabels" :key="f.value">
                          <input type="radio" class="btn-check" :id="`freq-${f.value}`" :value="f.value" v-model="form.simple.frequency" />
                          <label class="btn btn-outline-primary" :for="`freq-${f.value}`"><i :class="['bi', f.icon, 'me-1']"></i>{{ f.label }}</label>
                        </template>
                      </div>
                    </div>
                  </div>

                  <!-- Hourly: minute past the hour -->
                  <div v-if="form.simple.frequency === 'hourly'" class="row mb-3">
                    <label for="simpleMinute" class="col-sm-3 col-form-label">At minute</label>
                    <div class="col-sm-9">
                      <input id="simpleMinute" type="number" min="0" max="59" class="form-control" style="max-width: 8rem"
                        v-model.number="form.simple.minute" />
                      <div class="form-text">Minutes past each hour (0–59).</div>
                    </div>
                  </div>

                  <!-- Weekly: day chips -->
                  <div v-if="form.simple.frequency === 'weekly'" class="row mb-3">
                    <label class="col-sm-3 col-form-label">On days <span class="text-danger">*</span></label>
                    <div class="col-sm-9">
                      <div class="btn-group flex-wrap" role="group">
                        <template v-for="d in WEEKDAYS" :key="d.value">
                          <input type="checkbox" class="btn-check" :id="`wd-${d.value}`"
                            :checked="form.simple.weekdays.includes(d.value)" @change="toggleWeekday(d.value)" />
                          <label class="btn btn-outline-primary" :for="`wd-${d.value}`">{{ d.short }}</label>
                        </template>
                      </div>
                      <div v-if="errors.weekdays" class="text-danger small mt-1">{{ errors.weekdays }}</div>
                    </div>
                  </div>

                  <!-- Monthly: day of month -->
                  <div v-if="form.simple.frequency === 'monthly'" class="row mb-3">
                    <label for="simpleDom" class="col-sm-3 col-form-label">On day</label>
                    <div class="col-sm-9">
                      <input id="simpleDom" type="number" min="1" max="28" class="form-control" style="max-width: 8rem"
                        v-model.number="form.simple.dayOfMonth" />
                      <div class="form-text">Day of the month (1–28, to stay valid in every month).</div>
                    </div>
                  </div>

                  <!-- Time of day (all except hourly) -->
                  <div v-if="form.simple.frequency !== 'hourly'" class="row mb-3">
                    <label for="simpleTime" class="col-sm-3 col-form-label">At time</label>
                    <div class="col-sm-9">
                      <input id="simpleTime" type="time" class="form-control" style="max-width: 10rem" v-model="form.simple.time" />
                    </div>
                  </div>

                  <!-- Plain-language summary -->
                  <div class="row mb-3">
                    <div class="col-sm-9 offset-sm-3">
                      <p class="text-muted small mb-0"><i class="bi bi-calendar-check me-1"></i>{{ simpleSummary }}</p>
                    </div>
                  </div>
                </template>

                <div v-else class="row mb-3">
                  <label for="cronExpression" class="col-sm-3 col-form-label">Cron Expression <span class="text-danger">*</span></label>
                  <div class="col-sm-9">
                    <input id="cronExpression" type="text" class="form-control font-monospace" v-model="form.cronExpression"
                      :class="{ 'is-invalid': errors.cronExpression }" placeholder="0 9 * * *" />
                    <div class="form-text">Format: minute hour day month weekday</div>
                    <div v-if="errors.cronExpression" class="invalid-feedback">{{ errors.cronExpression }}</div>
                  </div>
                </div>

                <!-- Preview -->
                <div class="row mb-3">
                  <div class="col-sm-9 offset-sm-3">
                    <div class="alert mb-0" :class="previewError ? 'alert-danger' : 'alert-info'">
                      <h3 class="alert-heading mb-2 h6"><i class="bi bi-info-circle me-1"></i>Next runs</h3>
                      <div v-if="previewError">{{ previewError }}</div>
                      <ul v-else-if="nextRuns.length" class="mb-0 small">
                        <li v-for="r in nextRuns" :key="r">{{ new Date(r).toLocaleString() }}</li>
                      </ul>
                      <div v-else class="small text-muted">Enter a valid schedule to preview.</div>
                    </div>
                  </div>
                </div>

                <!-- Start / End window -->
                <div class="row mb-3">
                  <label for="startDate" class="col-sm-3 col-form-label">Start date</label>
                  <div class="col-sm-9">
                    <input id="startDate" type="datetime-local" class="form-control" v-model="form.startDateLocal" />
                    <div class="form-text">Optional — don't fire before this time.</div>
                  </div>
                </div>
                <div class="row mb-3">
                  <label for="endDate" class="col-sm-3 col-form-label">End date</label>
                  <div class="col-sm-9">
                    <input id="endDate" type="datetime-local" class="form-control" v-model="form.endDateLocal" />
                    <div class="form-text">Optional — auto-expire after this time.</div>
                  </div>
                </div>
              </template>

              <!-- Timezone -->
              <div class="row mb-3">
                <label for="timezone" class="col-sm-3 col-form-label">Timezone</label>
                <div class="col-sm-9">
                  <select id="timezone" class="form-select" v-model="form.timeZoneId">
                    <template v-for="g in timezoneGroupsWithOffsets" :key="g.label ?? 'root'">
                      <optgroup v-if="g.label" :label="g.label">
                        <option v-for="[id, name] in g.zones" :key="id" :value="id">{{ name }}</option>
                      </optgroup>
                      <option v-for="[id, name] in g.zones" v-else :key="id" :value="id">{{ name }}</option>
                    </template>
                  </select>
                </div>
              </div>

              <!-- Overlap behaviour -->
              <div class="row mb-3">
                <label class="col-sm-3 col-form-label">Overlapping runs</label>
                <div class="col-sm-9">
                  <div class="form-check">
                    <input
                      id="allowConcurrentRuns"
                      type="checkbox"
                      class="form-check-input"
                      v-model="form.allowConcurrentRuns"
                    />
                    <label class="form-check-label" for="allowConcurrentRuns">
                      Allow overlapping runs
                    </label>
                  </div>
                  <div class="form-text">
                    When off (default), a due run is skipped if the previous run is still in progress.
                  </div>
                </div>
              </div>

              <FormAlert :error="saveError" @dismiss="dismissSaveError" />
            </form>
          </div>
        </div>

        <!-- Run history (edit mode) -->
        <div v-if="isEditMode" class="card mt-4">
          <div class="card-body">
            <h2 class="card-title">Run History</h2>
            <p v-if="!scheduleRuns.length" class="text-muted mb-0">No runs yet.</p>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr><th>Fired</th><th>Scheduled for</th><th>Outcome</th><th>Run</th></tr>
                </thead>
                <tbody>
                  <tr v-for="r in scheduleRuns" :key="r.id">
                    <td>{{ new Date(r.firedAtUtc).toLocaleString() }}</td>
                    <td>{{ new Date(r.scheduledForUtc).toLocaleString() }}</td>
                    <td>
                      <span class="badge" :class="{
                        'bg-success': r.outcome === 'Fired',
                        'bg-warning': r.outcome === 'SkippedOverlap',
                        'bg-danger': r.outcome === 'Failed',
                      }">{{ r.outcome }}</span>
                      <span v-if="r.message" class="text-muted small ms-2">{{ r.message }}</span>
                    </td>
                    <td>
                      <RouterLink v-if="r.workflowRunId && schedule"
                        :to="`/projects/${schedule.projectId}/builder/${schedule.workflowId}/${r.workflowRunId}`"
                        class="small">View run</RouterLink>
                      <span v-else class="text-muted small">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-if="scheduleRuns.length >= RUN_LOG_CAP" class="text-muted small mb-0">
                <i class="bi bi-info-circle me-1"></i>Showing the {{ RUN_LOG_CAP }} most recent entries.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4 mt-4 mt-lg-0">
        <div class="sticky-top" style="top: 1rem">
          <div class="card mb-4">
            <div class="card-body">
              <h2 class="card-title">Actions</h2>
              <div class="d-grid gap-2">
                <button type="button" class="bb-btn-cancel" @click="emit('cancelled')"><i class="bi bi-x me-1"></i>Cancel</button>
                <button type="submit" form="schedule-form" class="bb-btn-primary-cta" :disabled="saving">
                  <span v-if="saving"><span class="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</span>
                  <span v-else><i class="bi bi-check me-1"></i>{{ isEditMode ? "Save Changes" : "Create Schedule" }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="isEditMode && schedule" class="card mb-4">
            <div class="card-body">
              <h2 class="card-title">Quick Actions</h2>
              <div class="d-grid gap-2">
                <button type="button" class="bb-btn-secondary-cta"
                  v-if="schedule.status === 'Active' || schedule.status === 'Paused'" @click="toggleScheduleStatus">
                  <i :class="['bi', schedule.status === 'Paused' ? 'bi-play' : 'bi-pause']" class="me-1"></i>
                  {{ schedule.status === "Paused" ? "Resume Schedule" : "Pause Schedule" }}
                </button>
                <button type="button" class="bb-btn-secondary-cta" @click="runNow"><i class="bi bi-play-fill me-1"></i>Run Now</button>
                <hr />
                <button type="button" class="bb-btn-danger" @click="showDeleteModal = true"><i class="bi bi-trash me-1"></i>Delete Schedule</button>
              </div>
            </div>
          </div>

          <div v-if="isEditMode && schedule" class="card">
            <div class="card-body">
              <h2 class="card-title">Current Status</h2>
              <div class="mb-3">
                <strong>Status:</strong>
                <span class="badge ms-2" :class="{
                  'bg-success': schedule.status === 'Active',
                  'bg-warning': schedule.status === 'Paused',
                  'bg-tertiary': ['Inactive', 'Completed', 'Expired'].includes(schedule.status),
                }">{{ schedule.status }}</span>
              </div>
              <div class="mb-3">
                <strong>Next Run:</strong><br />
                <small class="text-muted">{{ schedule.nextRunAtUtc ? new Date(schedule.nextRunAtUtc).toLocaleString() : "Not scheduled" }}</small>
              </div>
              <div v-if="schedule.lastRunError" class="mb-0">
                <strong>Last Error:</strong><br />
                <small class="text-danger">{{ schedule.lastRunError }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" :class="{ show: showDeleteModal }"
      :style="{ display: showDeleteModal ? 'block' : 'none' }" @click.self="showDeleteModal = false">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger"><i class="bi bi-exclamation-triangle me-2"></i>Delete Schedule</h2>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete <strong>{{ form.name }}</strong>?</p>
            <p class="text-muted mb-0">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
            <button class="btn btn-danger" @click="confirmDelete">Delete</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
  </div>
</template>

<style lang="scss" scoped>
.btn-group .btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn-group .btn-check:checked + .btn-outline-primary {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
}
</style>
